import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Timeline from './Timeline';
import { getHistories } from '../services/historyService';
import { getStudyState, submitStudyAnswer } from '../services/studyService';
import { getDisplayErrorMessage } from '../services/apiError';
import ErrorMessage from './ErrorMessage';

const Study = ({ user }) => {
  const navigate = useNavigate();
  const { historyId } = useParams();
  const [history, setHistory] = useState(null);
  const [studyState, setStudyState] = useState(null);
  const [firstDate, setFirstDate] = useState(null);
  const [lastDate, setLastDate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const histories = await getHistories();
        const currentHistory = histories.find(h => String(h.id) === historyId);
        setHistory(currentHistory);

        const state = await getStudyState(historyId);
        setStudyState(state);
      } catch (error) {
        console.error('Failed to load study data:', error);
        setError(getDisplayErrorMessage(error));
        setHistory(null);
        setStudyState(null);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [historyId]);

  const handleDatesFetched = useCallback((dates) => {
    setFirstDate(dates.first);
    setLastDate(dates.last);
  }, []);

  const handleAnswer = async (buttonValue) => {
    if (isSubmitting || !studyState) return;

    if (buttonValue === 'done') {
      navigate(`/?historyId=${historyId}`);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const newState = await submitStudyAnswer(studyState.cardId, buttonValue);
      setStudyState(newState);
    } catch (error) {
      console.error('Failed to submit answer:', error);
      setSubmitError(getDisplayErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <div className="p-8 flex-grow">Loading...</div>;
  }

  if (error) {
    return (
      <div className="p-8 flex-grow">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!history || !studyState) {
    return (
      <div className="p-8 flex-grow">
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
          <p>Study data not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-8 py-4 relative">
      <ErrorMessage message={submitError} onDismiss={() => setSubmitError(null)} />

      {studyState.stats && studyState.stats.length > 0 && (
        <div className="mb-2 px-3 py-2 bg-white rounded shadow inline-flex items-center gap-4 text-sm">
          {studyState.stats.map((section) => (
            <div key={section.section} className="flex items-center gap-2">
              <span className="font-semibold text-gray-700">{section.section}:</span>
              {section.items.map((item) => (
                <span key={item.title} className="text-gray-600">
                  {item.title}: <span className="font-medium text-gray-900">{item.value}</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      )}

      <div className="mb-8 p-6 bg-white rounded shadow">
        <h1 className="text-3xl font-bold">{history.name}</h1>
        <p className="text-sm">
          {firstDate ? (
            lastDate ? `${firstDate} — ${lastDate}` : firstDate
          ) : (
            'Loading...'
          )}
        </p>
      </div>

      <Timeline
        history={history}
        onDateFetched={handleDatesFetched}
        isOwner={false}
        studyState={studyState}
        onStudyAnswer={handleAnswer}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default Study;
